import { useState, useEffect } from "react";
import { getCompanySettings, updateCompanySettings, uploadLogo, exportBackupJson } from "../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Building2, 
  CreditCard, 
  Palette, 
  FileText, 
  Save,
  Upload,
  Download,
  Zap,
  CheckCircle,
  XCircle,
  Loader2,
  KeyRound,
  Copy,
  Check,
  Trash2,
  Plus,
  Eye,
  EyeOff,
  AlertTriangle
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("identity");

  const tabs = [
    { id: "identity", label: "Identité", icon: Building2 },
    { id: "payment", label: "Paiement", icon: CreditCard },
    { id: "branding", label: "Branding", icon: Palette },
    { id: "devis", label: "Devis", icon: FileText },
    { id: "factures", label: "Factures", icon: FileText },
    { id: "einvoice", label: "E-Facture", icon: Zap },
    { id: "api", label: "API", icon: KeyRound },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getCompanySettings();
      setSettings(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCompanySettings(settings);
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type = "main") => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const res = await uploadLogo(file, type);
      setSettings(prev => ({
        ...prev,
        [type === "white" ? "logo_white_url" : "logo_url"]: res.data.url
      }));
      toast.success("Logo uploadé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    }
  };

  const handleExportBackup = async () => {
    try {
      const res = await exportBackupJson();
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `backup_${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const [testingPdp, setTestingPdp] = useState(false);
  const [pdpTestResult, setPdpTestResult] = useState(null);

  const handleTestPdpConnection = async () => {
    setTestingPdp(true);
    setPdpTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/documents/einvoice/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPdpTestResult(data);
    } catch (err) {
      setPdpTestResult({ success: false, message: "Erreur de connexion" });
    } finally {
      setTestingPdp(false);
    }
  };

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setApiKeys(await res.json());
    } catch (err) {
      // silent
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) { toast.error("Nom requis"); return; }
    setCreatingKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newKeyName.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setNewlyCreatedKey(data);
        setNewKeyName("");
        setCopiedKey(false);
        loadApiKeys();
        toast.success("Clé API créée");
      } else {
        toast.error("Erreur lors de la création");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId, keyName) => {
    if (!window.confirm(`Révoquer la clé "${keyName}" ? Elle ne pourra plus être utilisée.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/api-keys/${keyId}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        loadApiKeys();
        toast.success("Clé révoquée");
      }
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDeleteApiKey = async (keyId, keyName) => {
    if (!window.confirm(`Supprimer définitivement la clé "${keyName}" ? Cette action est irréversible.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        loadApiKeys();
        toast.success("Clé supprimée");
      }
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    toast.success("Clé copiée");
    setTimeout(() => setCopiedKey(false), 3000);
  };

  // Load API keys when tab switches to "api"
  useEffect(() => {
    if (activeTab === "api") loadApiKeys();
  }, [activeTab]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Réglages
          </h1>
          <p className="text-slate-500 mt-1">Configuration de votre société</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleExportBackup}
            className="btn-outline"
            data-testid="export-backup-btn"
          >
            <Download size={16} className="mr-2" />
            Sauvegarder
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
            data-testid="save-settings-btn"
          >
            <Save size={16} className="mr-2" />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      {/* Custom Tabs - Improved visibility */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md transition-all ${
                isActive 
                  ? "bg-[#26788A] text-white shadow-sm" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-white"
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Identity Tab */}
        {activeTab === "identity" && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Informations société</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Nom commercial
                  </Label>
                  <Input
                    data-testid="company-name-input"
                    value={settings.company_name || ""}
                    onChange={(e) => updateField("company_name", e.target.value)}
                    className="mt-2 input-field"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Forme juridique
                  </Label>
                  <Input
                    data-testid="legal-form-input"
                    value={settings.legal_form || ""}
                    onChange={(e) => updateField("legal_form", e.target.value)}
                    className="mt-2 input-field"
                    placeholder="SASU, SARL, SAS..."
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Adresse
                </Label>
                <Input
                  data-testid="company-address-input"
                  value={settings.address || ""}
                  onChange={(e) => updateField("address", e.target.value)}
                  className="mt-2 input-field"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Code postal
                  </Label>
                  <Input
                    data-testid="company-postal-code-input"
                    value={settings.postal_code || ""}
                    onChange={(e) => updateField("postal_code", e.target.value)}
                    className="mt-2 input-field"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Ville
                  </Label>
                  <Input
                    data-testid="company-city-input"
                    value={settings.city || ""}
                    onChange={(e) => updateField("city", e.target.value)}
                    className="mt-2 input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Téléphone
                  </Label>
                  <Input
                    data-testid="company-phone-input"
                    value={settings.phone || ""}
                    onChange={(e) => updateField("phone", e.target.value)}
                    className="mt-2 input-field"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Email
                  </Label>
                  <Input
                    data-testid="company-email-input"
                    type="email"
                    value={settings.email || ""}
                    onChange={(e) => updateField("email", e.target.value)}
                    className="mt-2 input-field"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Site web
                </Label>
                <Input
                  data-testid="company-website-input"
                  value={settings.website || ""}
                  onChange={(e) => updateField("website", e.target.value)}
                  className="mt-2 input-field"
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    SIRET
                  </Label>
                  <Input
                    data-testid="company-siret-input"
                    value={settings.siret || ""}
                    onChange={(e) => updateField("siret", e.target.value)}
                    className="mt-2 input-field font-numbers"
                    placeholder="123 456 789 00012"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Code NAF
                  </Label>
                  <Input
                    data-testid="company-naf-input"
                    value={settings.naf_code || ""}
                    onChange={(e) => updateField("naf_code", e.target.value)}
                    className="mt-2 input-field font-numbers"
                    placeholder="5911A"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    TVA Intracom.
                  </Label>
                  <Input
                    data-testid="company-vat-input"
                    value={settings.vat_number || ""}
                    onChange={(e) => updateField("vat_number", e.target.value)}
                    className="mt-2 input-field font-numbers"
                    placeholder="FR12345678901"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Tab */}
        {activeTab === "payment" && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Coordonnées bancaires & Conditions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    IBAN
                  </Label>
                  <Input
                    data-testid="company-iban-input"
                    value={settings.iban || ""}
                    onChange={(e) => updateField("iban", e.target.value)}
                    className="mt-2 input-field font-numbers"
                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    BIC
                  </Label>
                  <Input
                    data-testid="company-bic-input"
                    value={settings.bic || ""}
                    onChange={(e) => updateField("bic", e.target.value)}
                    className="mt-2 input-field font-numbers"
                    placeholder="BNPAFRPP"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Conditions de paiement par défaut
                  </Label>
                  <Input
                    data-testid="payment-terms-input"
                    value={settings.default_payment_terms || ""}
                    onChange={(e) => updateField("default_payment_terms", e.target.value)}
                    className="mt-2 input-field"
                    placeholder="30 jours fin de mois"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Délai de paiement (jours)
                  </Label>
                  <Input
                    data-testid="payment-delay-input"
                    type="number"
                    value={settings.default_payment_delay_days || 30}
                    onChange={(e) => updateField("default_payment_delay_days", parseInt(e.target.value) || 30)}
                    className="mt-2 input-field font-numbers"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Taux de pénalités de retard (%)
                  </Label>
                  <Input
                    data-testid="late-penalty-rate-input"
                    type="number"
                    step="0.1"
                    value={settings.late_penalty_rate || 10}
                    onChange={(e) => updateField("late_penalty_rate", parseFloat(e.target.value) || 10)}
                    className="mt-2 input-field font-numbers"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Indemnité forfaitaire (€) - B2B
                  </Label>
                  <Input
                    data-testid="recovery-indemnity-input"
                    type="number"
                    step="1"
                    value={settings.recovery_indemnity || 40}
                    onChange={(e) => updateField("recovery_indemnity", parseFloat(e.target.value) || 40)}
                    className="mt-2 input-field font-numbers"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Texte pénalités de retard
                </Label>
                <Textarea
                  data-testid="late-penalty-text-input"
                  value={settings.late_penalty_text || ""}
                  onChange={(e) => updateField("late_penalty_text", e.target.value)}
                  className="mt-2 input-field min-h-20"
                  placeholder="En cas de retard de paiement..."
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Branding Tab */}
        {activeTab === "branding" && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Logo & Apparence</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider mb-4 block">
                    Logo principal (pour interface)
                  </Label>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50">
                    {settings.logo_url ? (
                      <img 
                        src={settings.logo_url} 
                        alt="Logo" 
                        className="max-h-24 mx-auto mb-4"
                      />
                    ) : (
                      <div className="text-slate-400 mb-4">Aucun logo</div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, "main")}
                        className="hidden"
                      />
                      <Button variant="outline" size="sm" className="btn-outline" asChild>
                        <span>
                          <Upload size={14} className="mr-2" />
                          Choisir un fichier
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider mb-4 block">
                    Logo blanc (pour PDF header noir)
                  </Label>
                  <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center bg-slate-900">
                    {settings.logo_white_url ? (
                      <img 
                        src={settings.logo_white_url} 
                        alt="Logo blanc" 
                        className="max-h-24 mx-auto mb-4"
                      />
                    ) : (
                      <div className="text-slate-500 mb-4">Aucun logo blanc</div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, "white")}
                        className="hidden"
                      />
                      <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" asChild>
                        <span>
                          <Upload size={14} className="mr-2" />
                          Choisir un fichier
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Préfixe Avoirs
                </Label>
                <Input
                  data-testid="credit-prefix-input"
                  value={settings.credit_prefix || "AV"}
                  onChange={(e) => updateField("credit_prefix", e.target.value)}
                  className="mt-2 input-field font-numbers w-32"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Devis Tab */}
        {activeTab === "devis" && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Paramètres des Devis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Préfixe Devis
                </Label>
                <Input
                  data-testid="quote-prefix-input"
                  value={settings.quote_prefix || "DV"}
                  onChange={(e) => updateField("quote_prefix", e.target.value)}
                  className="mt-2 input-field font-numbers w-32"
                />
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Mention par défaut (Devis)
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Cette mention apparaîtra automatiquement sur chaque nouveau devis. Elle peut être modifiée individuellement sur chaque devis.
                </p>
                <Textarea
                  data-testid="default-quote-mention-input"
                  value={settings.default_quote_mention || ""}
                  onChange={(e) => updateField("default_quote_mention", e.target.value)}
                  className="mt-2 input-field min-h-24"
                  placeholder="En cas de validation du devis, un acompte de 30 % est exigé..."
                />
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-700 mb-2">Mention par défaut suggérée</h4>
                <p className="text-xs text-blue-600 whitespace-pre-line">
                  En cas de validation du devis, un acompte de 30 % est exigé à la signature du devis.{"\n"}
                  Le solde est payable à la livraison de la prestation.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Factures Tab */}
        {activeTab === "factures" && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Paramètres des Factures</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Préfixe Factures
                </Label>
                <Input
                  data-testid="invoice-prefix-input"
                  value={settings.invoice_prefix || "FA"}
                  onChange={(e) => updateField("invoice_prefix", e.target.value)}
                  className="mt-2 input-field font-numbers w-32"
                />
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Mention par défaut (Factures)
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Cette mention apparaîtra automatiquement sur chaque nouvelle facture. Elle peut être modifiée individuellement sur chaque facture.
                </p>
                <Textarea
                  data-testid="default-invoice-mention-input"
                  value={settings.default_invoice_mention || ""}
                  onChange={(e) => updateField("default_invoice_mention", e.target.value)}
                  className="mt-2 input-field min-h-24"
                  placeholder="En votre aimable règlement."
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.b2b_mentions}
                    onChange={(e) => updateField("b2b_mentions", e.target.checked)}
                    className="rounded border-slate-300 text-[#26788A] focus:ring-[#26788A]"
                  />
                  <span className="text-slate-700 text-sm">Mentions B2B (indemnité forfaitaire)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.b2c_mentions}
                    onChange={(e) => updateField("b2c_mentions", e.target.checked)}
                    className="rounded border-slate-300 text-[#26788A] focus:ring-[#26788A]"
                  />
                  <span className="text-slate-700 text-sm">Mentions B2C</span>
                </label>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Préparation e-facture</h4>
                <p className="text-xs text-slate-500">
                  Cette version stocke les données structurées nécessaires à la facturation électronique. 
                  L'export JSON e-invoice et l'envoi PDP/PPF seront disponibles dans une future mise à jour.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* E-Facture Tab */}
        {activeTab === "einvoice" && (
          <div className="space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Zap size={20} className="text-[#26788A]" />
                  Configuration PDP (Plateforme de Dématérialisation)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-medium text-amber-800 mb-1">Réforme e-facture 2026</h4>
                  <p className="text-xs text-amber-700">
                    La facturation électronique devient obligatoire pour toutes les entreprises assujetties à la TVA. 
                    Configurez votre PDP partenaire pour assurer la conformité. En attendant, vos factures sont préparées 
                    au format Factur-X (XML CII) et peuvent être exportées manuellement.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600 text-xs uppercase tracking-wider">
                      Fournisseur PDP
                    </Label>
                    <select
                      value={settings.pdp_provider || "none"}
                      onChange={(e) => updateField("pdp_provider", e.target.value)}
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#26788A]"
                      data-testid="pdp-provider-select"
                    >
                      <option value="none">Aucune PDP</option>
                      <option value="chorus_pro">Chorus Pro (B2G)</option>
                      <option value="cegid">Cegid</option>
                      <option value="sage">Sage</option>
                      <option value="cegedim">Cegedim</option>
                      <option value="esker">Esker</option>
                      <option value="generix">Generix</option>
                      <option value="custom">Personnalisée</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-600 text-xs uppercase tracking-wider">
                      Mode
                    </Label>
                    <select
                      value={settings.pdp_mode || "test"}
                      onChange={(e) => updateField("pdp_mode", e.target.value)}
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#26788A]"
                      data-testid="pdp-mode-select"
                    >
                      <option value="test">Test</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                </div>

                {settings.pdp_provider && settings.pdp_provider !== "none" && (
                  <>
                    {settings.pdp_provider === "chorus_pro" && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-700">
                        <strong>Chorus Pro via PISTE</strong> — Créez un compte technique sur{" "}
                        <a href="https://developer.aife.economie.gouv.fr/" target="_blank" rel="noopener noreferrer" className="underline">PISTE</a>,
                        puis une application pour obtenir le Client ID / Secret. Le login technique est au format TECH_1_xxx@cpro.fr.
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-xs uppercase tracking-wider">
                          {settings.pdp_provider === "chorus_pro" ? "Client ID PISTE" : "Clé API PDP"}
                        </Label>
                        <Input
                          type="password"
                          value={settings.pdp_api_key || ""}
                          onChange={(e) => updateField("pdp_api_key", e.target.value)}
                          className="mt-2 input-field"
                          placeholder={settings.pdp_provider === "chorus_pro" ? "Client ID PISTE" : "Clé API"}
                          data-testid="pdp-api-key-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-xs uppercase tracking-wider">
                          {settings.pdp_provider === "chorus_pro" ? "Client Secret PISTE" : "Secret API PDP"}
                        </Label>
                        <Input
                          type="password"
                          value={settings.pdp_api_secret || ""}
                          onChange={(e) => updateField("pdp_api_secret", e.target.value)}
                          className="mt-2 input-field"
                          placeholder={settings.pdp_provider === "chorus_pro" ? "Client Secret PISTE" : "Secret API"}
                          data-testid="pdp-api-secret-input"
                        />
                      </div>
                    </div>

                    {settings.pdp_provider === "chorus_pro" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-600 text-xs uppercase tracking-wider">
                            Login technique Chorus Pro
                          </Label>
                          <Input
                            value={settings.chorus_tech_login || ""}
                            onChange={(e) => updateField("chorus_tech_login", e.target.value)}
                            className="mt-2 input-field"
                            placeholder="TECH_1_xxxxx@cpro.fr"
                            data-testid="chorus-tech-login-input"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-600 text-xs uppercase tracking-wider">
                            Mot de passe technique
                          </Label>
                          <Input
                            type="password"
                            value={settings.chorus_tech_password || ""}
                            onChange={(e) => updateField("chorus_tech_password", e.target.value)}
                            className="mt-2 input-field"
                            placeholder="Mot de passe du compte technique"
                            data-testid="chorus-tech-password-input"
                          />
                        </div>
                      </div>
                    )}

                    {settings.pdp_provider !== "chorus_pro" && (
                      <div>
                        <Label className="text-slate-600 text-xs uppercase tracking-wider">
                          URL Endpoint PDP
                        </Label>
                        <Input
                          value={settings.pdp_endpoint || ""}
                          onChange={(e) => updateField("pdp_endpoint", e.target.value)}
                          className="mt-2 input-field"
                          placeholder="https://api.votre-pdp.fr/v1"
                          data-testid="pdp-endpoint-input"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.pdp_enabled || false}
                          onChange={(e) => updateField("pdp_enabled", e.target.checked)}
                          className="rounded border-slate-300 text-[#26788A] focus:ring-[#26788A]"
                          data-testid="pdp-enabled-checkbox"
                        />
                        <span className="text-slate-700 text-sm">Activer la transmission PDP</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.auto_transmit || false}
                          onChange={(e) => updateField("auto_transmit", e.target.checked)}
                          className="rounded border-slate-300 text-[#26788A] focus:ring-[#26788A]"
                          data-testid="auto-transmit-checkbox"
                        />
                        <span className="text-slate-700 text-sm">Auto-transmettre après validation</span>
                      </label>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={handleTestPdpConnection}
                        disabled={testingPdp}
                        className="btn-outline"
                        data-testid="test-pdp-btn"
                      >
                        {testingPdp ? (
                          <Loader2 size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Zap size={16} className="mr-2" />
                        )}
                        Tester la connexion
                      </Button>
                      {pdpTestResult && (
                        <div className={`flex items-center gap-2 text-sm ${pdpTestResult.success ? 'text-green-600' : 'text-red-600'}`}>
                          {pdpTestResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                          {pdpTestResult.message}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Format et profil Factur-X</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600 text-xs uppercase tracking-wider">
                      Format par défaut
                    </Label>
                    <select
                      value={settings.default_einvoice_format || "factur_x"}
                      onChange={(e) => updateField("default_einvoice_format", e.target.value)}
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#26788A]"
                      data-testid="einvoice-format-select"
                    >
                      <option value="factur_x">Factur-X (PDF/A-3 + XML)</option>
                      <option value="ubl">UBL (Universal Business Language)</option>
                      <option value="cii">CII (Cross-Industry Invoice)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-600 text-xs uppercase tracking-wider">
                      Profil Factur-X
                    </Label>
                    <select
                      value={settings.facturx_profile || "basic"}
                      onChange={(e) => updateField("facturx_profile", e.target.value)}
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#26788A]"
                      data-testid="facturx-profile-select"
                    >
                      <option value="minimum">Minimum</option>
                      <option value="basic_wl">Basic WL</option>
                      <option value="basic">Basic</option>
                      <option value="en16931">EN 16931 (Norme européenne)</option>
                      <option value="extended">Extended</option>
                    </select>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Statut actuel</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">XML Factur-X</span>
                      <div className="flex items-center gap-1 mt-1 text-green-600 font-medium">
                        <CheckCircle size={14} />
                        Opérationnel
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-500">Connexion PDP</span>
                      <div className="flex items-center gap-1 mt-1 text-amber-600 font-medium">
                        {settings.pdp_provider && settings.pdp_provider !== "none" ? (
                          <>
                            <CheckCircle size={14} />
                            Configurée ({settings.pdp_mode || "test"})
                          </>
                        ) : (
                          <>
                            <XCircle size={14} />
                            Non configurée
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-500">Transmission auto</span>
                      <div className="flex items-center gap-1 mt-1 font-medium" style={{ color: settings.auto_transmit ? '#16A34A' : '#9CA3AF' }}>
                        {settings.auto_transmit ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {settings.auto_transmit ? "Activée" : "Désactivée"}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* API Tab */}
        {activeTab === "api" && (
          <div className="space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <KeyRound size={20} className="text-[#26788A]" />
                  Clés API
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-600">
                    Les clés API permettent à des services externes (Logyra RH, etc.) de créer des factures automatiquement.
                    Chaque clé est authentifiée via le header <code className="bg-slate-200 px-1 py-0.5 rounded text-[11px]">X-API-Key</code>.
                  </p>
                </div>

                {/* Create new key */}
                <div className="space-y-3">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label className="text-slate-600 text-xs uppercase tracking-wider">
                        Nouvelle clé
                      </Label>
                      <Input
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        className="mt-2 input-field"
                        placeholder="Nom du service (ex: Logyra RH Production)"
                        data-testid="new-api-key-name-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateApiKey()}
                      />
                    </div>
                    <Button
                      onClick={handleCreateApiKey}
                      disabled={creatingKey || !newKeyName.trim()}
                      className="btn-primary"
                      data-testid="create-api-key-btn"
                    >
                      {creatingKey ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
                      Générer
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Les clés sont permanentes et n'expirent jamais. Révoquez-les pour les désactiver ou supprimez-les définitivement.
                  </p>
                </div>

                {/* Newly created key alert */}
                {newlyCreatedKey && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2" data-testid="new-key-display">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-green-800">Clé créée : {newlyCreatedKey.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                      <span className="text-xs text-amber-700">Copiez cette clé maintenant. Elle ne sera plus affichée.</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-white border border-green-300 rounded px-3 py-2 text-sm font-mono text-slate-900 select-all break-all" data-testid="created-api-key-value">
                        {newlyCreatedKey.api_key}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        className={copiedKey ? "text-green-600 border-green-300" : "btn-outline"}
                        onClick={() => copyToClipboard(newlyCreatedKey.api_key)}
                        data-testid="copy-api-key-btn"
                      >
                        {copiedKey ? <Check size={16} /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Keys list */}
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider mb-3 block">
                    Clés existantes
                  </Label>
                  {apiKeysLoading ? (
                    <div className="text-center py-8 text-slate-400">
                      <Loader2 size={24} className="mx-auto animate-spin mb-2" />
                      Chargement...
                    </div>
                  ) : apiKeys.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      Aucune clé API. Créez-en une pour commencer.
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {apiKeys.map((key) => (
                        <div key={key.id} className="px-4 py-3 flex items-center justify-between" data-testid={`api-key-row-${key.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-900">{key.name}</span>
                              <Badge
                                className="text-[10px] px-1.5 py-0"
                                style={{
                                  backgroundColor: key.is_active ? '#dcfce7' : '#fee2e2',
                                  color: key.is_active ? '#166534' : '#991b1b',
                                }}
                              >
                                {key.is_active ? 'Active' : 'Révoquée'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                              <span className="font-mono">{key.key_prefix}...</span>
                              <span>{key.usage_count} appel{key.usage_count > 1 ? 's' : ''}</span>
                              {key.last_used_at && (
                                <span>Dernier : {new Date(key.last_used_at).toLocaleDateString("fr-FR")}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {key.is_active && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-600 border-amber-200 hover:bg-amber-50 h-7 text-xs"
                                onClick={() => handleRevokeApiKey(key.id, key.name)}
                                data-testid={`revoke-key-${key.id}`}
                              >
                                <EyeOff size={13} className="mr-1" />
                                Révoquer
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs"
                              onClick={() => handleDeleteApiKey(key.id, key.name)}
                              data-testid={`delete-key-${key.id}`}
                            >
                              <Trash2 size={13} className="mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* API Documentation card */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Documentation API</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-slate-600 space-y-3">
                  <p>Endpoints disponibles pour les services externes :</p>
                  <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-xs space-y-3 overflow-x-auto">
                    <div>
                      <span className="text-green-400">POST</span>{" "}
                      <span className="text-blue-300">/api/external/invoices</span>
                      <div className="text-slate-400 mt-1">Crée une facture validée + payée</div>
                    </div>
                    <div>
                      <span className="text-cyan-400">GET</span>{" "}
                      <span className="text-blue-300">/api/external/invoices/{"{id}"}/pdf</span>
                      <div className="text-slate-400 mt-1">Télécharge le PDF de la facture</div>
                    </div>
                    <div>
                      <span className="text-cyan-400">GET</span>{" "}
                      <span className="text-blue-300">/api/external/invoices/{"{id}"}</span>
                      <div className="text-slate-400 mt-1">Détails de la facture</div>
                    </div>
                    <div>
                      <span className="text-cyan-400">GET</span>{" "}
                      <span className="text-blue-300">/api/external/clients?email=xxx</span>
                      <div className="text-slate-400 mt-1">Recherche client (email, name, siret)</div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium mb-1">Authentification</p>
                    <code className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-slate-700">
                      X-API-Key: srk_votre_cle_ici
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
